import clsx from 'clsx';
import {
  formatDuration,
  formatRelativeTime,
  formatTimestamp,
  incidentTone,
  isTerminalStatus,
  statusBadge,
} from '../lib/incident-helpers';
import type { Incident } from '../lib/incidents-api';
import { IncidentStatusBadge } from './IncidentStatusBadge';

interface Props {
  incident: Incident;
  onBack: () => void;
}

export function IncidentDetail({ incident, onBack }: Props) {
  const headTone = incidentTone(incident);
  const subtitle = incident.resolvedAt
    ? `Resolved in ${formatDuration(incident.durationSec)}`
    : `Active · ${statusBadge(incident.status)}`;

  return (
    <section>
      <button
        type="button"
        onClick={onBack}
        className="mb-8 inline-flex items-center gap-1.5 text-[14px] font-medium leading-[1.4] text-ink-3 transition-colors duration-200 hover:text-accent"
      >
        <span aria-hidden>←</span> Incidents
      </button>

      <div className="mb-12 flex items-start gap-4">
        <div
          className={clsx(
            'mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-white min-[768px]:mt-2',
            headTone === 'ok' && 'bg-ok',
            headTone === 'warn' && 'bg-warn',
            headTone === 'down' && 'bg-down',
            headTone === 'info' && 'bg-info',
          )}
          aria-hidden
        >
          {isTerminalStatus(incident.status) ? (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2.5 7.3 L6 10.8 L11.5 4.7" />
            </svg>
          ) : (
            <span className="text-[14px] font-bold leading-none">!</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="m-0 break-words text-[32px] font-medium leading-[1.05] tracking-display text-ink-1 min-[768px]:text-[40px] min-[768px]:leading-[1.23]">
            {incident.title}
          </h1>
          <p className="mt-3 text-[16px] leading-[1.6] text-ink-3 min-[768px]:text-[18px]">
            {subtitle}
          </p>
          {incident.affectedComponents.length > 0 ? (
            <p className="mt-2 text-[14px] text-ink-3">
              Affected:&nbsp;
              {incident.affectedComponents.map((c, i) => (
                <span key={`${c.repoKey}-${c.suiteKey}-${c.network}`}>
                  {i > 0 ? ', ' : ''}
                  <code className="font-mono">{`${c.repoKey}/${c.suiteKey}`}</code>
                  <span className="ml-1">({c.network})</span>
                </span>
              ))}
            </p>
          ) : null}
        </div>
      </div>

      <h2 className="mb-4 text-[14px] font-semibold uppercase leading-none text-accent min-[768px]:text-[16px]">
        Incident timeline
      </h2>

      <div className="border border-line bg-paper-1">
        {incident.updates.length === 0 ? (
          <div className="px-5 py-6 text-[14px] text-ink-3">
            No updates posted yet.
          </div>
        ) : (
          incident.updates.map((update, idx) => {
            const updateTone = incidentTone({
              ...incident,
              status: update.status,
            });
            return (
              <div
                key={update.id}
                className={clsx(
                  'flex gap-3 px-5 py-5',
                  idx !== incident.updates.length - 1 && 'border-b border-line',
                )}
              >
                <div
                  className={clsx(
                    'mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-white',
                    updateTone === 'ok' && 'bg-ok',
                    updateTone === 'warn' && 'bg-warn',
                    updateTone === 'down' && 'bg-down',
                    updateTone === 'info' && 'bg-info',
                  )}
                  aria-hidden
                >
                  {isTerminalStatus(update.status) ? (
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 5.8 L4.5 8.3 L9 3.5" />
                    </svg>
                  ) : (
                    <span className="text-[11px] font-bold leading-none">!</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <IncidentStatusBadge status={update.status} />
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-[16px] leading-[1.6] text-ink-1">
                    {update.body}
                  </p>
                  <p className="mt-2 text-[14px] text-ink-3 [font-feature-settings:'tnum']">
                    {formatTimestamp(update.createdAt)} · {formatRelativeTime(update.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
